import type { DeckProject } from './types'
import { migrateProject } from './migration'
import { assertDeckProject, ProjectValidationError } from './project-validation'
import { downloadBlob, newId, toFileSlug } from './utils'

const PROJECT_FILE = 'project.json'
const MAX_PROJECT_ARCHIVE_BYTES = 512 * 1024 * 1024

export type ProjectFileErrorKind = 'empty' | 'too-large' | 'archive' | 'missing-project' | 'json' | 'incompatible' | 'damaged'

export class ProjectFileError extends Error {
  readonly kind: ProjectFileErrorKind
  readonly details: string[]

  constructor(kind: ProjectFileErrorKind, message: string, details: string[] = []) {
    super(message)
    this.name = 'ProjectFileError'
    this.kind = kind
    this.details = details
  }
}

export const exportProjectFile = async (project: DeckProject) => {
  assertDeckProject(project)
  const { default: JSZip } = await import('jszip')
  const zip = new JSZip()
  zip.file(
    PROJECT_FILE,
    JSON.stringify(
      {
        format: 'deckelier-project',
        exportedAt: new Date().toISOString(),
        project,
      },
      null,
      2,
    ),
  )
  const blob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 9 },
  })
  downloadBlob(blob, `${toFileSlug(project.name)}.deckelier`)
}

export const importProjectFile = async (file: File): Promise<DeckProject> => {
  if (file.size === 0) throw new ProjectFileError('empty', 'This project file is empty.')
  if (file.size > MAX_PROJECT_ARCHIVE_BYTES) {
    throw new ProjectFileError('too-large', 'This project is too large to open safely in a browser.', ['The compressed file exceeds Deckelier’s 512 MB beta limit.'])
  }
  const { default: JSZip } = await import('jszip')
  let zip: Awaited<ReturnType<typeof JSZip.loadAsync>>
  try {
    zip = await JSZip.loadAsync(await file.arrayBuffer())
  } catch {
    throw new ProjectFileError('archive', 'This is not a readable Deckelier project archive.', ['The file may be incomplete, renamed, or damaged.'])
  }
  const entry = zip.file(PROJECT_FILE)
  if (!entry) throw new ProjectFileError('missing-project', 'This archive does not contain a Deckelier project.', [`The required ${PROJECT_FILE} entry is missing.`])
  let source: string
  try {
    source = await entry.async('text')
  } catch {
    throw new ProjectFileError('archive', 'Deckelier could not read the project data inside this archive.')
  }
  let payload: { format?: string; project?: DeckProject }
  try {
    payload = JSON.parse(source) as typeof payload
  } catch {
    throw new ProjectFileError('json', 'The project data is not valid JSON.', ['The archive may have been truncated or manually edited.'])
  }
  if (payload.format !== 'deckelier-project' || !payload.project) {
    throw new ProjectFileError('incompatible', 'This file is not a supported Deckelier project.', ['Its format marker or project payload is missing.'])
  }
  let imported: DeckProject
  try {
    imported = migrateProject(payload.project)
  } catch (error) {
    if (error instanceof ProjectValidationError) {
      throw new ProjectFileError('damaged', error.message, error.issues.map((issue) => `${issue.path}: ${issue.message}`))
    }
    throw new ProjectFileError(
      /not supported/i.test(error instanceof Error ? error.message : '') ? 'incompatible' : 'damaged',
      error instanceof Error ? error.message : 'This Deckelier project is damaged.',
    )
  }
  const now = new Date().toISOString()
  imported.id = newId('project')
  imported.name = `${imported.name} (imported)`
  imported.createdAt = now
  imported.updatedAt = now
  return imported
}
